import React, { useState } from 'react';
import { OpenInNewIcon } from 'components/Icons';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { message as antMessage } from 'antd';
import showRecordBanner from 'screens/Applications&WorkOrders/Components/showRecordBanner';
import { OperationType } from 'components/RecordsListView/constants';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes/paths';
import {
  RecordSource,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useOperationEvents from 'hooks/useOperationEvents';
import useInvoiceFlags from 'screens/Invoices/redesign/hooks/useInvoiceFlags';
import { RECORD_SOURCE_MESSAGE_KEY } from './constants/messageKey';
import MessageContent from './components/MessageContent';
import useRecordValues from './hooks/useRecordValues';

interface RecordSourceButtonProps {
  recordSource: RecordSource;
  type: OperationType;
  /** Id of the Application/Work Order currently being viewed, used to return here. */
  operationId?: string;
}

// Recommendations and Plans open a full-page view with no DetailPanel, so
// there is no panel to anchor to and no readiness signal to wait for -- they
// keep the original immediate, unanchored banner. Invoices only qualify on the
// redesigned landing; the legacy one has no panel either.
const DESTINATIONS_WITH_DETAIL_PANEL = new Set([RecordSourceType.WORK_ORDER]);

const RecordSourceButton: React.FC<RecordSourceButtonProps> = ({
  recordSource,
  type,
  operationId
}) => {
  const {
    icon,
    isClickable,
    tooltipTitle,
    recordSourceName = recordSource.recordSourceName,
    onClick
  } = useRecordValues(recordSource);

  const history = useHistory();
  const { sendAccessRecordSourceEvent } = useOperationEvents(type);
  const { feInvoicesActive } = useInvoiceFlags();

  const handleClick = () => {
    if (isClickable) {
      sendAccessRecordSourceEvent(recordSource.recordSourceType, type);

      const onReturn = () => {
        const returnPath =
          type === OperationType.APPLICATION
            ? paths.applications
            : paths.workOrders;
        // Only the id is known here (not the operation's name) — SummaryParamsContext's
        // `resolveOperationName` fetches the name by id and turns it into the same
        // pseudo-search filter used when navigating to a Work Order record source.
        history.push({
          pathname: returnPath,
          state: { operationId }
        });
      };

      onClick?.();
      const messageContent = <MessageContent type={type} onReturn={onReturn} />;

      const anchorsToDetailPanel =
        DESTINATIONS_WITH_DETAIL_PANEL.has(
          recordSource.recordSourceType as RecordSourceType
        ) ||
        (recordSource.recordSourceType === RecordSourceType.INVOICE &&
          feInvoicesActive);

      const bannerConfig = {
        key: RECORD_SOURCE_MESSAGE_KEY,
        className: `ant-message-dark-mode br-4${
          anchorsToDetailPanel ? ' record-banner-anchored' : ''
        }`,
        content: messageContent,
        duration: 0,
        icon: <></>
      };

      if (anchorsToDetailPanel) {
        showRecordBanner(bannerConfig);
      } else {
        antMessage.success(bannerConfig);
      }
    }
  };

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isDeleted = recordSource.deleted;

  if (isClickable) {
    return (
      <div
        style={{ height: '20px', position: 'relative' }}
        className="flex items-center gap-2"
      >
        <div className="flex items-center min-w-0">
          <Tooltip
            title={tooltipTitle}
            placement="top"
            align={{ offset: [0, 2] }}
            arrowPointAtCenter
            overlayInnerStyle={{
              backgroundColor: '#333'
            }}
            open={isTooltipOpen}
          >
            <span
              style={{
                position: 'absolute',
                left: '9px',
                top: 0,
                width: '1px',
                height: '100%',
                pointerEvents: 'none'
              }}
            />
          </Tooltip>
          <button
            type="button"
            onClick={isDeleted ? undefined : handleClick}
            onMouseEnter={() =>
              isDeleted ? undefined : setIsTooltipOpen(true)
            }
            onMouseLeave={() =>
              isDeleted ? undefined : setIsTooltipOpen(false)
            }
            style={{
              height: '24px',
              left: '-0.5rem',
              marginRight: '-0.5rem',
              position: 'relative'
            }}
            className={`flex items-center gap-1 px-2 py-0.5 rounded overflow-clip min-w-0 ${
              isDeleted
                ? 'bg-neutral-10 focus:outline-hidden cursor-default'
                : 'hover:bg-blue-10 cursor-pointer'
            } transition-colors`}
          >
            <div className="flex items-center shrink-0">{icon}</div>
            <div
              className={`text-sm min-w-0 flex-1 truncate ${
                isDeleted ? 'text-neutral-50' : 'text-blue-60'
              } font-normal leading-5 tracking-tight`}
            >
              {recordSourceName}
            </div>
            {!isDeleted && (
              <div className="flex items-center shrink-0">
                <OpenInNewIcon fill="#0071CD" />
              </div>
            )}
          </button>
        </div>
        {isDeleted && (
          <div className="flex items-center text-sm text-neutral-70 font-normal leading-5 tracking-tight whitespace-nowrap">
            (Deleted)
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 py-0.5 rounded overflow-clip min-w-0">
      <div className="flex items-center shrink-0">{icon}</div>
      <div className="text-sm text-neutral-1000 font-normal leading-5 tracking-tight min-w-0 flex-1 truncate">
        {recordSourceName}
      </div>
    </div>
  );
};

export default RecordSourceButton;
